import KitPlayerPrompt from '@omni/kit/components/KitPlayerPrompt';
import usePlayerPromptSize from '@omni/kit/components/KitPlayerPrompt/usePlayerPromptSize';
import KitTouchPassThroughView from '@omni/kit/components/KitTouchPassThroughView';
import { useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import Spacing from '@omni/kit/theming/Spacing';
import React, {
  ReactElement,
  RefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { usePlayerVideoCurrentTime } from './PlayerVideo';
import { PlayerVideoRef } from './PlayerVideo/PlayerVideo.types';

const PLAYER_PROMPT_WRAP_PADDING = Spacing.xl;

export interface PlayerPromptRef {
  update: () => void;
}

interface PlayerTimelineProps {
  appKey: string;
  isLoading: boolean;
  isPlaying: boolean;
  mediaItemShortCode: string;
  playerVideoRef?: RefObject<PlayerVideoRef | null>;
}

export default forwardRef<PlayerPromptRef, PlayerTimelineProps>(
  (
    { appKey, isLoading, isPlaying, mediaItemShortCode, playerVideoRef },
    playerPromptRef
  ): ReactElement => {
    const { currentTime, updateTime } = usePlayerVideoCurrentTime({
      playerVideoRef,
    });

    const [
      isScreenSmallerThanPlayerPrompt,
      setIsScreenSmallerThanPlayerPrompt,
    ] = useState(false);

    const { windowWidth } = useSizeClass();
    const { maxWidth } = usePlayerPromptSize();

    useImperativeHandle(playerPromptRef, () => ({
      update: updateTime,
    }));

    /**
     * wrap in useEffect to ensure the window width is calculated during client side render
     * to avoid nextJS version staleness
     * https://nextjs.org/docs/messages/version-staleness
     */
    useEffect(() => {
      setIsScreenSmallerThanPlayerPrompt(
        windowWidth < maxWidth + PLAYER_PROMPT_WRAP_PADDING * 2
      );
    }, [windowWidth, maxWidth]);

    return (
      <KitTouchPassThroughView
        style={{
          alignItems: isScreenSmallerThanPlayerPrompt ? 'center' : 'flex-end',
          display: 'flex',
          height: '100%',
          justifyContent: 'flex-start',
          paddingBottom: PLAYER_PROMPT_WRAP_PADDING,
          paddingHorizontal: PLAYER_PROMPT_WRAP_PADDING,
          paddingTop: PLAYER_PROMPT_WRAP_PADDING,
          position: 'absolute',
          width: '100%',
        }}
      >
        <KitPlayerPrompt
          appKey={appKey}
          durationMsec={0}
          isLiveStream={true}
          isNativeBuffering={isLoading}
          isPlaying={isPlaying}
          isSeekable={true}
          isSeeking={false}
          mediaItemShortCode={mediaItemShortCode}
          playId={`liveChat.${Date.now()}`}
          styles={{
            marginHorizontal: 0,
            overflow: 'hidden',
          }}
          positionMsec={currentTime * 1000}
        />
      </KitTouchPassThroughView>
    );
  }
);
